import { motion, useReducedMotion } from "framer-motion";
import { Receipt, CreditCard, Check } from "lucide-react";

/**
 * Looped animation for the Invoicing & card payments card:
 * an invoice goes from Sent → Paid, and a "Card charged" toast pops in
 * to consume it. Mirrors CreditFlowAnimation's shape and rhythm.
 */
export function InvoicePaidAnimation({ className = "" }: { className?: string }) {
  const reduce = useReducedMotion();
  const duration = 3; // seconds per full loop

  const statuses = reduce
    ? [{ label: "Paid", paid: true }]
    : [
        { label: "Sent", paid: false },
        { label: "Paid", paid: true },
      ];

  return (
    <div
      className={`relative w-full max-w-md mx-auto rounded-xl border bg-card p-5 ${className}`}
      aria-hidden="true"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Receipt className="h-5 w-5 text-brand-text" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Invoice #1042</div>
          <div className="text-xs text-muted-foreground">Sarah J.</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums text-brand-text">$120</div>
          <div className="relative h-4 w-16 ml-auto overflow-hidden">
            {statuses.map((s, i) => (
              <motion.span
                key={s.label + "-" + i}
                className={`absolute inset-0 flex items-center justify-end gap-1 text-[10px] font-semibold uppercase tracking-wide ${
                  s.paid ? "text-brand-text" : "text-muted-foreground"
                }`}
                initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
                animate={
                  reduce
                    ? { opacity: 1, y: 0 }
                    : { opacity: [0, 1, 1, 0], y: [-10, 0, 0, 10] }
                }
                transition={
                  reduce
                    ? undefined
                    : {
                        duration,
                        times: [0, 0.15, 0.85, 1],
                        repeat: Infinity,
                        delay: (i * duration) / statuses.length,
                        ease: "easeInOut",
                      }
                }
              >
                {s.paid && <Check className="h-3 w-3" />}
                {s.label}
              </motion.span>
            ))}
          </div>
        </div>
      </div>

      {/* Card-charged toast that pops in to mark it Paid */}
      <motion.div
        className="mt-4 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
        initial={reduce ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
        animate={
          reduce
            ? { opacity: 1, x: 0 }
            : { opacity: [0, 1, 1, 0], x: [-20, 0, 0, 20] }
        }
        transition={
          reduce
            ? undefined
            : {
                duration: duration * statuses.length,
                times: [0, 0.3, 0.6, 0.7],
                repeat: Infinity,
                ease: "easeInOut",
              }
        }
      >
        <CreditCard className="h-3.5 w-3.5 text-brand-text" />
        <span className="font-medium">Card charged</span>
        <span className="text-muted-foreground">— $120.00</span>
      </motion.div>
    </div>
  );
}
